import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { useSelector } from "react-redux";
import { useMutation } from "react-query";
import axios from "axios";
import { toast } from "react-toastify";
import Table from "../../Components/Settings_onprogress/Table";
import {
  BoxHead,
  BoxContainer,
  BoxTitle,
  Underline,
} from "../../Components/Styles/PageStyles";
import RentingStayRulesModal from "../../Components/Settings_onprogress/RentingStayRulesModal";
import ManagementDetailsModal from "../../Components/Settings_onprogress/ManagementDetailsModal";
import NotificationSettings from "../../Components/Settings_onprogress/NotificationSettings";
import OnboardingSettingsModal from "../../Components/Settings_onprogress/OnboardingSettings";
import EvictionSettingsModal from "../../Components/Settings_onprogress/EvictionSettingsModal";
import Logout from "../../Components/Logout";
import Config from "../../Config";
import SideModal from "../../Components/SideModal";
import RenewAgreementRulesModal from "../../Components/Settings_onprogress/RenewAgreementRulesModal";
import DuesSettingsModal from "../../Components/Settings_onprogress/DuesSettingsModal";
import ReceiptSettingsModal from "../../Components/Settings_onprogress/ReceiptSettingsModal";
import TellySettingsModal from "../../Components/Settings_onprogress/TellySettingsModal";
import GlobalSettingsModal from "../../Components/Settings_onprogress/GlobalSettingModal";

const TableWrapper = tw.h1`my-10 rounded-md`;

const Settings = () => {
  const user = useSelector((state) => state.UserReducer.user);
  const property = useSelector(
    (state) => state.SelectPropertyReducer.selectProperty
  );
  const propertyList = useSelector((state) => state.PropertyReducer.property);
  const [openModal, setOpenModal] = useState(null);
  const [isLogout, setIsLogout] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [modalStates, setModalStates] = useState({
    managementModel: false,
    rentModel: false,
    notiModel: false,
    onboardingModel: false,
    evictionModel: false,
    duesModel: false,
    renewAgreementModel: false,
    receiptModal: false,
    tellyModal: false,
    globalModal: false,
  });
  const [selectedProperties, setSelectedProperties] = useState([]);
  const getAllPropertyIds = () => {
    if (!propertyList || propertyList.length === 0) return "";

    const validProperties = propertyList.filter((prop) => prop.id !== 0);
    return validProperties.map((prop) => prop.id).join(",");
  };

  const getPropertyParam = (endpoint = "", selectedPropertyId = null) => {
    if (selectedPropertyId) {
      return selectedPropertyId;
    }
    if (property?.id === 0 && (endpoint === "/property/web/renting/rules/" || "/client/rent/renewal/setting/list/")) {
      return propertyList[1]?.id || "";
    }
    if (property?.id === 0) {
      return getAllPropertyIds();
    }
    return property?.id;
  };

  const updateModalState = (modalKey, value) => {
    setModalStates((prev) => ({
      ...prev,
      [modalKey]: value,
    }));
  };

  const getHeaders = () => ({
    authorization: `Bearer ${user.token}`,
    "Content-Type": "application/json",
  });

  const fetchSettings = async (endpoint, selectedPropertyId = null) => {
    const propertyParam = getPropertyParam(endpoint, selectedPropertyId);
    const response = await axios.get(
      `${Config.apiUrl}${endpoint}${propertyParam}`,
      { headers: getHeaders() }
    );
    return response.data;
  };

  // Management settings mutation
  const {
    mutate: fetchManagementSettingsMutate,
    isLoading: fetchManagementSettingsLoading,
    refetch
  } = useMutation(() => fetchSettings("/property/web/management/details/"), {
    onSuccess: (res) => {
      if (res?.isSuccess) {
        setSettingsData(res?.data);
        updateModalState("managementModel", true);
      }
    },
  });

  // Rent settings mutation
  const {
    mutate: fetchRentSettingsMutate,
    isLoading: fetchRentSettingsLoading,
  } = useMutation(
    (selectedPropertyId) =>
      fetchSettings("/property/web/renting/rules/", selectedPropertyId),
    {
      onSuccess: (res) => {
        if (res?.isSuccess) {
          setSettingsData(res?.data);
          updateModalState("rentModel", true);
          setSelectedProperties([])
        }
      },
      onError: (error) => {
        toast.error("Error fetching property details");
      },
    }
  );

  // Renew Agreement settings mutation
  const {
    mutate: fetchAgreementSettingsMutate,
    isLoading: fetchAgreementSettingsLoading,
  } = useMutation(
    (selectedPropertyId) =>
      fetchSettings("/client/rent/renewal/setting/list?propId=", selectedPropertyId),
    {
      onSuccess: (res) => {
        if (res?.isSuccess) {
          setSettingsData(res?.data);
          updateModalState("renewAgreementModel", true);
          setSelectedProperties([])
        }
      },
      onError: (error) => {
        toast.error("Error fetching property details");
      },
    }
  );


  // Notification settings mutation
  const {
    mutate: fetchNotiSettingsMutate,
    isLoading: fetchNotifySettingsLoading,
  } = useMutation((selectedPropertyId) => fetchSettings("/settings/list/", selectedPropertyId), {
    onSuccess: (res) => {
      if (res?.isSuccess) {
        setSettingsData(res?.data);
        updateModalState("notiModel", true);
      }
    },
  });

  // Onboarding settings mutation - UPDATED to accept propertyId
  const {
    mutate: fetchOnboardingSettingsMutate,
    isLoading: fetchOnboardingSettingsLoading,
  } = useMutation(
    (selectedPropertyId) =>
      fetchSettings("/client/tenant/onboarding/setting/list?propId=", selectedPropertyId),
    {
      onSuccess: (res) => {
        if (res?.isSuccess) {
          setSettingsData(res?.data);
          updateModalState("onboardingModel", true);
          setSelectedProperties([])
        }
      },
    }
  );

  // Eviction settings mutation
  const {
    mutate: fetchEvictionSettingsMutate,
    isLoading: fetchEvictionSettingsLoading,
  } = useMutation(
    (selectedPropertyId) =>
      fetchSettings("/client/eviction/setting/list?propId=", selectedPropertyId),
    {
      onSuccess: (res) => {
        if (res?.isSuccess) {
          setSettingsData(res?.data);
          updateModalState("evictionModel", true);
          setSelectedProperties([])
        }
      },
    }
  );

  // Dues settings mutation
  const {
    mutate: fetchDuesSettingsMutate,
    isLoading: fetchDuesSettingsLoading,
  } = useMutation(
    (selectedPropertyId) =>
      fetchSettings("/client/finance/setting/list?propId=", selectedPropertyId),
    {
      onSuccess: (res) => {
        if (res?.isSuccess) {
          setSettingsData(res?.data);
          updateModalState("duesModel", true);
          setSelectedProperties([])
        }
      },
    }
  );


  const {
    mutate: fetchReceiptSettingsMutate,
    isLoading: fetchReceiptSettingsLoading,
  } = useMutation(
    () => {
      return axios.get(`${Config.apiUrl}/client/receipt/terms/list`, {
        headers: getHeaders()
      });
    },
    {
      onSuccess: (res) => {
        if (res?.data?.isSuccess) {
          setSettingsData(res?.data);
          updateModalState("receiptModal", true);
          setSelectedProperties([])
        }
      },
    }
  );

  // Global settings mutation
  const {
    mutate: fetchGlobalSettingsMutate,
    isLoading: fetchGlobalSettingsLoading,
  } = useMutation(
    () => {
      return axios.get(`${Config.apiUrl}/client/global/setting/list`, {
        headers: getHeaders()
      });
    },
    {
      onSuccess: (res) => {
        if (res?.data?.isSuccess) {
          setSettingsData(res?.data?.data);
          updateModalState("globalModal", true);
          setSelectedProperties([])
        }
      },
    }
  );

  const {
    mutate: fetchTellySettingsMutate,
    isLoading: fetchTellySettingsLoading,
  } = useMutation(
    () => {
      return axios.get(`${Config.apiUrl}/client/tally/setting/list`, {
        headers: getHeaders()
      });
    },
    {
      onSuccess: (res) => {
        if (res?.data?.isSuccess) {
          setSettingsData(res?.data?.data);
          updateModalState("tellyModal", true);
          setSelectedProperties([])
        }
      },
    }
  );

  const {
    mutate: syncTallyDataMutate,
    isLoading: syncTallyDataLoading,
  } = useMutation(
    () => {
      return axios.get(`${Config.apiUrl}/client/tally/sync`, {
        headers: getHeaders()
      });
    },
    {
      onSuccess: (res) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.message || "Tally data synced successfully!");
          // You can also refetch settings data here if needed
          // fetchTellySettingsMutate(); // If you want to refresh the settings after sync
        } else {
          toast.error(res?.data?.message || "Failed to sync Tally data");
        }
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || "Failed to sync Tally data");
      },
    }
  );
  // Update notification settings mutation
  const { mutate: editNotiSettingsMutate, isLoading: editNotiSettingsLoading } =
    useMutation(
      (values) => {
        const payload = {
          propIds: values.propIds.join(","), // Changed from propId to propIds
          whatsApp: values.whatsApp || 0,
          sms: values.sms || 0,
          rentReminder: values.rentReminder || 0,
          onboardWelcome: values.onboardWelcome || 0,
        };
        return axios.post(`${Config.apiUrl}/settings/edit/new`, payload, {
          headers: getHeaders(),
        });
      },
      {
        onSuccess: (res) => {
          if (res?.data?.isSuccess) {
            toast.success(res?.data?.msg || "Settings updated successfully.");
          } else {
            toast.error(res?.data?.msg || "Failed to update settings.");
          }
        },
        onError: () => {
          toast.error("Error updating settings.");
        },
      }
    );

  // Update management details mutation
  const {
    mutate: editManagementDetailMutate,
    isLoading: editManagementDetailLoading,
  } = useMutation(
    (values) => {
      const propertyParam = getPropertyParam();
      const payload = {
        propId: propertyParam,
        name: values.name,
        email: values.email,
        mobile: values.mobile,
        businessName: values.businessName,
        gstNo: values.gstNo || "",
      };
      return axios.post(
        `${Config.apiUrl}/property/web/management/details/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg);
          updateModalState("managementModel", false);
          setOpenModal(null);
        }
      },
    }
  );

  // Update renting details mutation
  const {
    mutate: editRentingDetailMutate,
    isLoading: editRentingDetailLoading,
  } = useMutation(
    (submitData) => {
      const payload = {
        propId: submitData.selectedProperties.join(","),
        rent: submitData.rent,
        security: submitData.security,
        rentalCycle: submitData.rentalCycle,
        lockInPeriod: submitData.lockInPeriod,
        noticePeriod: submitData.noticePeriod,
        agreementPeriod: submitData.agreementDuration || "",
        // replicate: submitData.replicate || 0,
      };
      return axios.post(
        `${Config.apiUrl}/property/renting/rules/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res, variables) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg);
          setIsEditing(false)
          let propertyToFetch;
          if (variables?.selectedPropertyId) {
            propertyToFetch = variables.selectedPropertyId;
          } else if (variables?.propIds && !variables.propIds.includes(',')) {
            propertyToFetch = variables.propIds;
          } else {
            propertyToFetch = getPropertyParam("/property/web/renting/rules/");
          }
          // const propId =
          //   variables?.selectedPropertyId ||
          //   getPropertyParam("/property/web/renting/rules/");
          setSelectedProperties([])
          fetchRentSettingsMutate(propertyToFetch);

          // updateModalState("rentModel", false);
          // setOpenModal(null);
        }
      },
    }
  );

  // Update renew agreement details mutation
  const {
    mutate: editAgreementDetailMutate,
    isLoading: editAgreementDetailLoading,
  } = useMutation(

    (submitData) => {
      const payload = {
        propIds: submitData.selectedProperties.join(","),
        updateSecurity: submitData.updateSecurity,
        agreementCharges: submitData.agreementCharges,
        rentIncrementType: submitData.rentIncrementType,
        rentIncrement: submitData.rentIncrement,
        autoRenew: submitData.autoRenew,
        // replicate: submitData.replicate || 0,
      };
      return axios.post(
        `${Config.apiUrl}/client/rent/renewal/setting/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res, variables) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg);
          setIsEditing(false)
          let propertyToFetch;
          if (variables?.selectedPropertyId) {
            propertyToFetch = variables.selectedPropertyId;
          } else if (variables?.propIds && !variables.propIds.includes(',')) {
            propertyToFetch = variables.propIds;
          } else {
            propertyToFetch = getPropertyParam("/client/rent/renewal/setting/list?propId=");
          }
          // const propId =
          //   variables?.selectedPropertyId ||
          //   getPropertyParam("/client/rent/renewal/setting/list?propId=");
          setSelectedProperties([])
          fetchAgreementSettingsMutate(propertyToFetch);

          // updateModalState("renewAgreementModel", false);
          // setOpenModal(null);
        }
      },
    }
  );



  const {
    mutate: editOnboardSettingsMutate,
    isLoading: editOnboardSettingsLoading,
  } = useMutation(
    (values) => {
      const payload = {
        type: values.type,
        value: values.value,
        ekyc: values.ekyc,
        onlinePayment: values.onlinePayment,
        policeVerification: values.policeVerification,
        rentAgreement: values.rentAgreement,
        skipKyc: values.skipKyc,
        propIds: values.propIds.join(","),
        // replicate: values.replicate || 0,
      };
      return axios.post(
        `${Config.apiUrl}/client/tenant/onboarding/setting/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res, variables) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg || "Settings updated successfully.");
          // let propertyToFetch;
          // if (variables?.selectedPropertyId) {
          //   propertyToFetch = variables.selectedPropertyId;
          // } else if (variables?.propIds && !variables.propIds.includes(',')) {
          //   propertyToFetch = variables.propIds;
          // } else {
          //   propertyToFetch = getPropertyParam("/client/tenant/onboarding/setting/list?propId=");
          // }
          // setSelectedProperties([]);
          // fetchOnboardingSettingsMutate(propertyToFetch);

        } else {
          toast.error(res?.data?.msg || "Failed to update settings.");
        }
      },
      onError: () => {
        toast.error("Error updating settings.");
      },
    }
  );

  // Update eviction settings mutation
  const {
    mutate: editEvictionSettingsMutate,
    isLoading: editEvictionSettingsLoading,
  } = useMutation(
    (values) => {
      const payload = {
        lockInServe: values.lockInServe,
        clearDues: values.clearDues,
        midCycleCharge: values.midCycleCharge,
        restrictMoveOut: values.restrictMoveOut,
        propIds: values.propIds.join(","),
      };
      return axios.post(
        `${Config.apiUrl}/client/eviction/setting/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res, variables) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg || "Eviction settings updated successfully.");
          // let propertyToFetch;
          // if (variables?.selectedPropertyId) {
          //   propertyToFetch = variables.selectedPropertyId;
          // } else if (variables?.propIds && !variables.propIds.includes(',')) {
          //   propertyToFetch = variables.propIds;
          // } else {
          //   propertyToFetch = getPropertyParam("/client/eviction/setting/list?propId=");
          // }
          // setSelectedProperties([]);
          // fetchEvictionSettingsMutate(propertyToFetch);
        } else {
          toast.error(res?.data?.msg || "Failed to update eviction settings.");
        }
      },
      onError: () => {
        toast.error("Error updating eviction settings.");
      },
    }
  );

  // Update dues settings mutation
  const {
    mutate: editDuesSettingsMutate,
    isLoading: editDuesSettingsLoading,
  } = useMutation(
    (values) => {
      const payload = {
        canHideDues: values.canHideDues,
        // autoReminders: values.autoReminders,
        partialPayment: values.partialPayment,
        manualReminders: values.manualReminders,
        expenseAllocationByBed: values.expenseAllocationByBed,
        propIds: values.propIds.join(","),
        // replicate: values.replicate || 0,
      };
      return axios.post(
        `${Config.apiUrl}/client/finance/setting/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res, variables) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg || "Dues settings updated successfully.");
          // let propertyToFetch;
          // if (variables?.selectedPropertyId) {
          //   propertyToFetch = variables.selectedPropertyId;
          // } else if (variables?.propIds && !variables.propIds.includes(',')) {
          //   propertyToFetch = variables.propIds;
          // } else {
          //   propertyToFetch = getPropertyParam("/client/finance/setting/list?propId=");
          // }
          // setSelectedProperties([]);

          // fetchDuesSettingsMutate(propertyToFetch);

        } else {
          toast.error(res?.data?.msg || "Failed to update dues settings.");
        }
      },
      onError: () => {
        toast.error("Error updating dues settings.");
      },
    }
  );


  // Update global settings mutation
  const {
    mutate: editGlobalSettingsMutate,
    isLoading: editGlobalSettingsLoading,
  } = useMutation(
    (values) => {
      const payload = {
        manualMoveIn: values.manualMoveIn,
        manualMoveOut: values.manualMoveOut,
        autoAssignComplaint: values.autoAssignComplaint,
        bookingAmt: values.bookingAmt,
      };
      return axios.post(
        `${Config.apiUrl}/client/global/setting/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res, variables) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg || "Global settings updated successfully.");
          setSelectedProperties([]);
          // fetchGlobalSettingsMutate();
        } else {
          toast.error(res?.data?.msg || "Failed to update Global settings.");
        }
      },
      onError: () => {
        toast.error("Error updating global settings.");
      },
    }
  );

  // Update receipt settings mutation
  const {
    mutate: editReceiptSettingsMutate,
    isLoading: editReceiptSettingsLoading,
  } = useMutation(
    (values) => {
      const payload = {
        // propIds: values.propIds.join(","),
        receiptTerms: values.description,
        stayRules: values.stayRules


      };
      return axios.post(
        `${Config.apiUrl}/client/receipt/terms/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res, variables) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg || "Receipt settings updated successfully.");
          setSelectedProperties([]);
          // fetchReceiptSettingsMutate();
        } else {
          toast.error(res?.data?.msg || "Failed to update receipt settings.");
        }
      },
      onError: () => {
        toast.error("Error updating dues settings.");
      },
    }
  );

  // Update Telly settings mutation
  const {
    mutate: editTellySettingsMutate,
    isLoading: editTellySettingsLoading,
  } = useMutation(
    (values) => {
      const payload = {
        ip: values.ip,
        port: values.port,
        billRefPrefix: values.billRefPrefix,
        isTallyEnabled: values.isTallyEnabled,
      };
      return axios.post(
        `${Config.apiUrl}/client/tally/setting/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res, variables) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg || "Receipt telly updated successfully.");
          setSelectedProperties([]);
          setIsEditing(false)
          fetchTellySettingsMutate();
        } else {
          toast.error(res?.data?.msg || "Failed to update telly settings.");
        }
      },
      onError: (res) => {
        toast.error(res?.response?.data?.msg || "Failed to update telly settings.");
      },
    }
  );

  useEffect(() => {
    if (!openModal) return;

    const modalActions = {
      ManagementDetails: fetchManagementSettingsMutate,
      RentingStayRules: fetchRentSettingsMutate,
      NotificationSettings: () => {
        if (property?.id !== 0) {
          fetchNotiSettingsMutate(property?.id);
        }
      },
      // NotificationSettings: fetchNotiSettingsMutate,
      OnboardingSettings: () => {
        if (property?.id !== 0) {
          fetchOnboardingSettingsMutate(property?.id);
        }
      },
      EvictionSettings: () => {
        if (property?.id !== 0) {
          fetchEvictionSettingsMutate(property?.id);
        }
      },
      ReceiptSettings: () => {
        fetchReceiptSettingsMutate()
        // if (property?.id !== 0) {
        //   fetchReceiptSettingsMutate(property?.id);
        // }
      },
      TellySettings: () => {
        fetchTellySettingsMutate()
      },
      // RenewAgreement: fetchAgreementSettingsMutate,
      RenewAgreement: () => {
        const propId = getPropertyParam("/client/rent/renewal/setting/list/");
        fetchAgreementSettingsMutate(propId);
      },
      DuesSettings: () => {
        if (property?.id !== 0) {
          fetchDuesSettingsMutate(property?.id);
        }
      },
      GlobalSettings: () => {
        fetchGlobalSettingsMutate();
      }
    };

    if (modalActions[openModal]) {
      modalActions[openModal]();
    }
  }, [openModal]);

  const handleOpenModal = (modal) => {
    Object.keys(modalStates).forEach((key) => updateModalState(key, false));
    setOpenModal(modal);
  };

  const handleCloseModal = () => {
    setOpenModal(null);
    setIsEditing(false)
  };

  return (
    <>
      <BoxContainer className="!h-auto">
        <BoxHead>
          <BoxTitle className="!mb-0 whitespace-nowrap">
            Settings
            <Underline />
          </BoxTitle>
        </BoxHead>

        <TableWrapper>
          <Table
            setOpenModal={handleOpenModal}
            settingsData={settingsData}
            setIsLogout={setIsLogout}
            property={property}
            propertyList={propertyList}
          />
        </TableWrapper>
        <SideModal
          isOpen={openModal}
          onClose={() => {
            setOpenModal(false);
            setIsEditing(false);
          }}
        >
          {openModal === "ManagementDetails" && (
            <ManagementDetailsModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              managementSettingsData={settingsData || {}}
              editManagementDetailMutate={editManagementDetailMutate}
              editManagementDetailLoading={editManagementDetailLoading}
              isOpen={openModal === "ManagementDetails"}
              onClose={() => {
                updateModalState("managementModel", false);
                setOpenModal(null);
              }}
              property={property}
              propertyList={propertyList}
              fetchManagementSettingsLoading={fetchManagementSettingsLoading}
              isLoading={fetchManagementSettingsLoading}
              isDataLoaded={modalStates.managementModel}
            />
          )}

          {/* Renting Stay Rules Modal */}
          {openModal === "RentingStayRules" && (
            <RentingStayRulesModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              editRentingDetailMutate={editRentingDetailMutate}
              editRentingDetailLoading={editRentingDetailLoading}
              settingsData={settingsData || {}}
              isOpen={openModal === "RentingStayRules"}
              onClose={handleCloseModal}
              property={property}
              propertyList={propertyList}
              propertyParam={getPropertyParam("/property/web/renting/rules/")}
              rentingDetailsMutate={fetchRentSettingsMutate}
              isLoading={fetchRentSettingsLoading}
              isDataLoaded={modalStates.rentModel}
              setIsEditing={setIsEditing}
              isEditing={isEditing}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
            />
          )}

          {/* Renew Agreement Modal */}
          {openModal === "RenewAgreement" && (
            <RenewAgreementRulesModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              editAgreementDetailMutate={editAgreementDetailMutate}
              editAgreementDetailLoading={editAgreementDetailLoading}
              settingsData={settingsData || {}}
              isOpen={openModal === "RenewAgreement"}
              onClose={handleCloseModal}
              property={property}
              propertyList={propertyList}
              propertyParam={getPropertyParam("/client/rent/renewal/setting/list/")}
              rentingDetailsMutate={fetchAgreementSettingsMutate}
              isLoading={fetchAgreementSettingsLoading}
              isDataLoaded={modalStates.renewAgreementModel}
              setIsEditing={setIsEditing}
              isEditing={isEditing}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
            />
          )}

          {/* Eviction Settings Modal */}
          {openModal === "EvictionSettings" && (
            <EvictionSettingsModal
              // isOpen={openModal === "EvictionSettings"}
              // onClose={handleCloseModal}
              onClose={() => {
                updateModalState("evictionModel", false);
                setOpenModal(null);
              }}
              settingsData={settingsData || {}}
              isLoading={editEvictionSettingsLoading}
              editSettingsMutate={editEvictionSettingsMutate}
              property={property}
              propertyList={propertyList}
              fetchSettingsLoading={fetchEvictionSettingsLoading}
              isDataLoaded={modalStates.evictionModel}
              fetchEvictionSettingsMutate={fetchEvictionSettingsMutate}
              propertyParam={property?.id !== 0 ? property?.id : null}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
            />
          )}

          {/* Notification Settings Modal */}
          {openModal === "NotificationSettings" && (
            <NotificationSettings
              isOpen={openModal === "NotificationSettings"}
              onClose={handleCloseModal}
              editNotiSettingsMutate={editNotiSettingsMutate}
              editNotiSettingsLoading={editNotiSettingsLoading}
              settingsData={settingsData || {}}
              property={property}
              propertyList={propertyList}
              fetchNotifySettingsLoading={fetchNotifySettingsLoading}
              isDataLoaded={modalStates.notiModel}
              fetchNotiSettingsMutate={fetchNotiSettingsMutate}
              propertyParam={property?.id !== 0 ? property?.id : null}
            />
          )}

          {/* Onboarding Settings Modal */}
          {openModal === "OnboardingSettings" && (
            <OnboardingSettingsModal
              onClose={() => {
                updateModalState("onboardingModel", false);
                setOpenModal(null);
              }}
              settingsData={settingsData || {}}
              isLoading={editOnboardSettingsLoading}
              editSettingsMutate={editOnboardSettingsMutate}
              editSettingsLoading={editOnboardSettingsLoading}
              property={property}
              propertyList={propertyList}
              fetchSettingsLoading={fetchOnboardingSettingsLoading}
              isDataLoaded={modalStates.onboardingModel}
              fetchOnboardingSettingsMutate={fetchOnboardingSettingsMutate}
              propertyParam={property?.id !== 0 ? property?.id : null}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
            />
          )}

          {/* Dues Settings Modal */}
          {openModal === "DuesSettings" && (
            <DuesSettingsModal
              // isOpen={openModal === "DuesSettings"}
              // onClose={handleCloseModal}
              onClose={() => {
                updateModalState("duesModel", false);
                setOpenModal(null);
              }}
              settingsData={settingsData || {}}
              isLoading={editDuesSettingsLoading}
              editSettingsMutate={editDuesSettingsMutate}
              editSettingsLoading={editDuesSettingsLoading}
              property={property}
              propertyList={propertyList}
              fetchSettingsLoading={fetchDuesSettingsLoading}
              isDataLoaded={modalStates.duesModel}
              fetchDuesSettingsMutate={fetchDuesSettingsMutate}
              propertyParam={property?.id !== 0 ? property?.id : null}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
            />
          )}

          {openModal === "ReceiptSettings" && (
            <ReceiptSettingsModal
              // isOpen={openModal === "DuesSettings"}
              // onClose={handleCloseModal}
              onClose={() => {
                updateModalState("receiptModal", false);
                setOpenModal(null);
              }}
              settingsData={settingsData || {}}
              isLoading={editReceiptSettingsLoading}
              editSettingsMutate={editReceiptSettingsMutate}
              editSettingsLoading={editReceiptSettingsLoading}
              property={property}
              propertyList={propertyList}
              fetchSettingsLoading={fetchReceiptSettingsLoading}
              isDataLoaded={modalStates.duesModel}
              fetchReceiptSettingsMutate={fetchReceiptSettingsMutate}
              propertyParam={property?.id !== 0 ? property?.id : null}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
            />
          )}

          {openModal === "GlobalSettings" && (
            <GlobalSettingsModal
              // isOpen={openModal === "DuesSettings"}
              // onClose={handleCloseModal}
              onClose={() => {
                updateModalState("globalModal", false);
                setOpenModal(null);
              }}
              settingsData={settingsData || {}}
              isLoading={editGlobalSettingsLoading}
              editSettingsMutate={editGlobalSettingsMutate}
              editSettingsLoading={editGlobalSettingsLoading}
              fetchSettingsLoading={fetchGlobalSettingsLoading}
              isDataLoaded={modalStates.globalModal}
            />
          )}

          {openModal === "TellySettings" && (
            <TellySettingsModal
              // isOpen={openModal === "DuesSettings"}
              // onClose={handleCloseModal}
              onClose={() => {
                updateModalState("tellyModal", false);
                setIsEditing(false)
                setOpenModal(null);
              }}
              settingsData={settingsData || {}}
              isLoading={editReceiptSettingsLoading}
              editSettingsMutate={editTellySettingsMutate}
              editSettingsLoading={editTellySettingsLoading}
              property={property}
              propertyList={propertyList}
              fetchSettingsLoading={fetchTellySettingsLoading}
              isDataLoaded={modalStates.tellyModal}
              fetchTellySettingsMutate={fetchTellySettingsMutate}
              propertyParam={property?.id !== 0 ? property?.id : null}
              selectedProperties={selectedProperties}
              setSelectedProperties={setSelectedProperties}
              setIsEditing={setIsEditing}
              isEditing={isEditing}
              syncTallyDataMutate={syncTallyDataMutate}
              syncTallyDataLoading={syncTallyDataLoading}
            />
          )}
        </SideModal>

        {/* Logout Modal */}
        {isLogout && <Logout isLogout={isLogout} setIsLogout={setIsLogout} />}
      </BoxContainer>
    </>
  );
};

export default Settings;
